import React, { useState, useRef, useEffect } from 'react';
import { Animated, TouchableOpacity, ScrollView } from 'react-native';
import { router, usePathname } from 'expo-router';
import styled from '@emotion/native';
import { sand } from '@/shared/styles/colors';
import {
  MOBILE_WEB_MENU_ITEMS,
  MOBILE_MENU_ITEMS,
} from '@/shared/constants/navigation';
import MobileDrawerItem from './MobileMenuItem';
import { IS_WEB } from '@/shared/constants/utility';
import useDimensions from '@/shared/hooks/useDimensions';
import { ROUTES } from '@/shared/constants/routes';
import { BOTTOM_TAB_BAR_HEIGHT } from '@/shared/styles/constants';
import { useGetUser } from '@/shared/hooks/useGetUser';
import { useIsAdmin } from '@/shared/hooks/useIsAdmin';
import AddIcon from '../AddIcon';
import EditIcon from '../EditIcon';

interface MobileMenuProps {
  visible: boolean;
}

export default function MobileMenu({ visible }: MobileMenuProps) {
  const { width } = useDimensions();
  const currentPath = usePathname();
  const { user } = useGetUser();

  const animation = useRef(new Animated.Value(width)).current;
  const [shouldRender, setShouldRender] = useState(visible);

  const { isAdmin } = useIsAdmin();
  const menuItems = (IS_WEB ? MOBILE_WEB_MENU_ITEMS : MOBILE_MENU_ITEMS).filter(
    (item) => !item.requiresAdmin || isAdmin,
  );

  useEffect(() => {
    if (visible) {
      setShouldRender(true);
      Animated.timing(animation, {
        toValue: 0,
        duration: 250,
        useNativeDriver: true,
      }).start();
    } else {
      Animated.timing(animation, {
        toValue: width,
        duration: 250,
        useNativeDriver: true,
      }).start(() => {
        setShouldRender(false);
      });
    }
  }, [visible, width]);

  if (!shouldRender) return null;

  return (
    <AnimatedDrawer
      style={{
        transform: [{ translateX: animation }],
        top: 0,
      }}
    >
      <ScrollView
        bounces={false}
        style={{
          flex: 1,
          backgroundColor: sand,
        }}
      >
        <DrawerContent>
          {!IS_WEB && (
            <OuterImageContainer>
              <TouchableOpacity
                onPress={() => router.push(ROUTES.PROFILE.path)}
              >
                <ImageContainer>
                  {user?.profilePicUrl ? (
                    <>
                      <ProfileImage source={{ uri: user?.profilePicUrl }} />
                      <EditIconContainer>
                        <EditIcon />
                      </EditIconContainer>
                    </>
                  ) : (
                    <AddIcon />
                  )}
                </ImageContainer>
              </TouchableOpacity>
            </OuterImageContainer>
          )}
          {menuItems.map((item, index) => (
            <MobileDrawerItem
              key={item.label}
              label={item.label}
              route={item.route}
              leadingIcon={item.leadingIcon}
              trailingIcon={item.trailingIcon}
              isLast={index === menuItems.length - 1}
              isActive={
                typeof item.route === 'object' &&
                'path' in item.route &&
                item.route.path === currentPath
              }
            />
          ))}
        </DrawerContent>
      </ScrollView>
    </AnimatedDrawer>
  );
}

const AnimatedDrawer = styled(Animated.View)({
  position: 'absolute',
  bottom: 0,
  right: 0,
  width: '100%',
  backgroundColor: sand,
});

const DrawerContent = styled.View({
  backgroundColor: sand,
  flex: 1,
  paddingBottom: BOTTOM_TAB_BAR_HEIGHT + 60,
});

const OuterImageContainer = styled.View({
  width: '100%',
  alignItems: 'center',
  marginBottom: 24,
  marginTop: 32,
});

const ImageContainer = styled.View({
  width: 152,
  height: 157,
  borderRadius: 6,
  justifyContent: 'center',
  alignItems: 'center',
});

export const ProfileImage = styled.Image({
  width: '100%',
  height: '100%',
  borderRadius: 6,
});

export const EditIconContainer = styled.View({
  position: 'absolute',
  bottom: 9,
  right: 6,
  borderRadius: 6,
});
